import { defineStore } from 'pinia';
import { onRequestDefaultError, requests } from '🔗/utils/requests';

const defaultEvents = (state, entity) => ({
    preview: async() => await state.preview(entity),
    list: async(params = {}) => await state.list(entity, params),
    check: async(id) => await state.check(entity, id),
    like: async(id) => await state.like(entity, id),
    dislike: async(id) => await state.dislike(entity, id),
    remove: async(id) => await state.remove(entity, id)
});


const useFavoriteApi = defineStore('favorite-api', {
    state: () => ({
        reactions: {
            artists: {},
            playlists: {},
            tracks: {},
            releases: {},
        },
        previews: {},
    }),
    getters: {
        artists: (state) => defaultEvents(state, 'artists'),
        playlists: (state) => defaultEvents(state, 'playlists'),
        tracks: (state) => defaultEvents(state, 'tracks'),
        releases: (state) => defaultEvents(state, 'releases')
    },
    actions: {
        async preview(entity) {
            if ([undefined, null].indexOf(this.previews[entity]) === -1) {
                return this.previews[entity];
            }

            this.previews[entity] = await this.list(entity, { skip: 0, take: 4 });

            return this.previews[entity];
        },
        async list(entity, params = {}) {
            try {
                const result = await this.$http({
                    method: 'GET',
                    url: `/user/favorite/${entity}`,
                    params,
                    signal: requests.signal(`favorite-${entity}`)
                });

                if (result.status === 200) {
                    const { data } = result;

                    return data;
                }
            } catch (e) {
                onRequestDefaultError(e);
            }
        },
        async check(entity, id) {
            try {
                const result = await this.$http({
                    method: 'GET',
                    url: `/user/favorite/${entity}/check/?id=${ id }`,
                    // signal: requests.signal('favorite-${entity}-check', id)
                });

                if (result.status === 200) {
                    const { data } = result;

                    this.reactions[entity][id] = data;

                    return data;
                }
            } catch (e) {
                onRequestDefaultError(e);
            }
        },
        async like(entity, id) {
            try {
                const result = await this.$http({
                    method: 'POST',
                    url: `/user/favorite/${entity}/${ id }/like`,
                    signal: requests.signal(`favorite-${entity}-like`, id)
                });

                if (result.status === 200) {
                    const { data } = result;

                    if (data?.success) {
                        this.reactions[entity][id] = 'LIKE';
                        delete this.previews[entity];
                    }

                    return data;
                }
            } catch (e) {
                onRequestDefaultError(e);
            }
        },
        async dislike(entity, id) {
            try {
                const result = await this.$http({
                    method: 'POST',
                    url: `/user/favorite/${entity}/${ id }/dislike`,
                    signal: requests.signal(`favorite-${entity}-dislike`, id)
                });

                if (result.status === 200) {
                    const { data } = result;

                    if (data?.success) {
                        this.reactions[entity][id] = 'DISLIKE';
                        delete this.previews[entity];
                    }

                    return data;
                }
            } catch (e) {
                onRequestDefaultError(e);
            }
        },
        async remove(entity, id) {
            try {
                const result = await this.$http({
                    method: 'DELETE',
                    url: `/user/favorite/${entity}/${ id }`,
                    signal: requests.signal(`favorite-${entity}-remove`, id)
                });

                if (result.status === 200) {
                    const { data } = result;

                    if (data?.success) {
                        delete this.reactions[entity][id];
                        delete this.previews[entity];
                    }

                    return data;
                }
            } catch (e) {
                onRequestDefaultError(e);
            }
        },

        // Proxy
        async getArtists(params) {
            return await this.list('artists', params);
        },
        async getPlaylists(params) {
            return await this.list('playlists', params);
        },
        async getTracks(params) {
            return await this.list('tracks', params);
        },
        async getReleases(params) {
            return await this.list('releases', params);
        }
    }
});

export default useFavoriteApi;
